import { Link } from 'react-router-dom';
import { H2, P, Ul, Code, CodeBlock } from '../../components/docs/primitives';

const FOLDER_LAYOUT = `my-plugin/
├── plugin.toml      identity, runtime, requested capabilities
├── my-plugin.exe    the entry executable (any language)
└── assets/          anything else the plugin needs`;

const MINIMAL_MANIFEST = `id          = "yourhandle.my-plugin"
name        = "My Plugin"
version     = "1.0.0"
author      = "yourhandle"
tier        = "A"
description = "One or two plain sentences about what it does."
host_min    = "8.0.0"

[runtime]
kind      = "process"
entry     = "my-plugin.exe"
transport = "stdio"

[capabilities]
events       = ["on_watch_tick"]
host_methods = ["notify", "log"]
network      = "none"`;

const FRAME = `Content-Length: <byte count>\\r\\n
\\r\\n
<exactly that many bytes of UTF-8 JSON>`;

export default function FirstPlugin() {
  return (
    <>
      <H2 id="layout">The folder</H2>
      <P>
        A plugin is one directory: a manifest at the root, an entry executable, and whatever else
        you need. Any language works as long as it can read stdin and write stdout.
      </P>
      <CodeBlock code={FOLDER_LAYOUT} />

      <H2 id="manifest">A minimal manifest</H2>
      <P>
        <Code>plugin.toml</Code> declares what the plugin is and everything it may ask the host
        for. Request only what you use: the consent dialog shows every line to the user. The{' '}
        <Link to="/plugins/docs/manifest" className="text-accent hover:underline">
          manifest reference
        </Link>{' '}
        covers every field.
      </P>
      <CodeBlock code={MINIMAL_MANIFEST} />

      <H2 id="run">Run it inside StreamNook</H2>
      <P>
        No packaging is needed during development. In StreamNook open Settings, then Plugins, then
        Develop, and register the folder that contains your <Code>plugin.toml</Code>. The app
        treats it like an installed plugin, with the same capability grants and the same consent
        dialogs, so what you see is what your users will see.
      </P>

      <H2 id="implement">What you must implement</H2>
      <P>
        Messages are JSON-RPC 2.0 envelopes over stdin and stdout, one per frame, with a
        byte-length header:
      </P>
      <CodeBlock code={FRAME} />
      <P>The host expects four things from every plugin:</P>
      <Ul>
        <li>
          Answer the <Code>initialize</Code> request within 10 seconds. Its params tell you which
          capabilities the user actually granted; you reply with your version and the events you
          want delivered.
        </li>
        <li>
          Answer <Code>ping</Code> with <Code>{'{}'}</Code>. It arrives every 30 seconds, and
          three missed responses get you restarted.
        </li>
        <li>
          Answer <Code>shutdown</Code>, then exit when the <Code>exit</Code> notification arrives.
          A process still alive 5 seconds later is killed.
        </li>
        <li>
          Tolerate partial grants. Users can approve a subset of what you request; degrade or
          report through <Code>log</Code>, do not crash.
        </li>
      </Ul>

      <H2 id="state">Logs and state</H2>
      <Ul>
        <li>
          <Code>initialize</Code> hands you a <Code>data_dir</Code> for persistent state and a{' '}
          <Code>log_dir</Code> holding your log file.
        </li>
        <li>
          Write diagnostics through the <Code>log</Code> method or stderr; both end up in your log
          file. Use <Code>notify</Code> only for things the user must see.
        </li>
        <li>
          Events emitted while your process was down are not replayed. After every start, rebuild
          your view of the world from host methods like <Code>get_followed_live</Code>.
        </li>
      </Ul>
    </>
  );
}
